import React from 'react';
import HrnbButton from './HrnbButton';
import { BackIcon, ForwardIcon } from '../../icons';
import '../../css/components/controls/HrnbPagination.css';

/**
 * Переиспользуемый компонент пагинации с номерами страниц
 * @param {Object} props
 * @param {number} props.currentPage - Текущая страница (1-indexed)
 * @param {number} props.totalPages - Всего страниц
 * @param {function} props.onPageChange - Колбэк при смене страницы
 * @param {number} [props.maxVisible=5] - Максимальное количество видимых номеров страниц
 * @param {boolean} [props.showInfo=true] - Показывать ли текст «Страница X из Y»
 */
const HrnbPagination = ({
    currentPage,
    totalPages,
    onPageChange,
    maxVisible = 5,
    showInfo = true,
}) => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
        const pages = [];
        const halfVisible = Math.floor(maxVisible / 2);

        let startPage = Math.max(1, currentPage - halfVisible);
        let endPage = Math.min(totalPages, currentPage + halfVisible);

        if (currentPage <= halfVisible) {
            endPage = Math.min(totalPages, maxVisible);
        }
        if (currentPage + halfVisible >= totalPages) {
            startPage = Math.max(1, totalPages - maxVisible + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return pages;
    };

    const pageNumbers = getPageNumbers();
    const showStartEllipsis = pageNumbers[0] > 1;
    const showEndEllipsis = pageNumbers[pageNumbers.length - 1] < totalPages;

    return (
        <div className="hrnb-pagination">
            {showInfo && (
                <span className="hrnb-pagination__info">
                    Страница {currentPage} из {totalPages}
                </span>
            )}
            <div className="hrnb-pagination__controls">
                <HrnbButton
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    icon={<BackIcon />}
                    aria-label="Предыдущая страница"
                />

                {/* Первая страница + многоточие */}
                {showStartEllipsis && (
                    <>
                        <button
                            className="hrnb-pagination__page"
                            onClick={() => onPageChange(1)}
                        >
                            1
                        </button>
                        {pageNumbers[0] > 2 && (
                            <span className="hrnb-pagination__ellipsis">…</span>
                        )}
                    </>
                )}

                {/* Номера страниц */}
                {pageNumbers.map((page) => (
                    <button
                        key={page}
                        className={`hrnb-pagination__page ${page === currentPage ? 'hrnb-pagination__page--active' : ''}`}
                        onClick={() => onPageChange(page)}
                    >
                        {page}
                    </button>
                ))}

                {/* Многоточие + последняя страница */}
                {showEndEllipsis && (
                    <>
                        {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
                            <span className="hrnb-pagination__ellipsis">…</span>
                        )}
                        <button
                            className="hrnb-pagination__page"
                            onClick={() => onPageChange(totalPages)}
                        >
                            {totalPages}
                        </button>
                    </>
                )}

                <HrnbButton
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    icon={<ForwardIcon />}
                    aria-label="Следующая страница"
                />
            </div>
        </div>
    );
};

export default HrnbPagination;
